import { computed, observable } from 'mobx';

import { Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { makeTableQuery, TableQuery } from '../../adl-gen/common/tabular';
import {
  AppClient,
  ApplicationRole,
  AppUser,
  AppUserStatus,
  makeUserGroupManager,
  makeUserGroupMember,
  makeUserQuiz,
  Quiz,
  UserGroupManager,
  UserGroupMember,
  UserQuiz
} from '../../adl-gen/totalinteraction/futurefit/db';
import { AppUserListEntry } from '../../adl-gen/totalinteraction/futurefit/frontend';
import { UserData } from '../../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';

import { DataStore, TransportLayer, UserGroupChange } from './index';

/// Moved from user-quiz-store.ts - no separate store required
/// TODO: update - ie to DueAt field
export type UserQuizChange =
  | {
      kind: 'loaded' | 'update';
      value: WithDbId<UserQuiz>;
    }
  | {
      kind: 'create';
      value: DbKey<Quiz>;
      userId?: DbKey<AppUser>;
      // dueAt?: Instant
    }
  | {
      kind: 'delete' | 'purge';
      value: DbKey<UserQuiz>;
      // dueAt?: Instant
    };

class AppUserApi implements TransportLayer<AppUser> {
  kind: string = 'AppUser';
  constructor(protected readonly service: Service) {}
  create = async (item: AppUser): Promise<DbKey<AppUser>> => this.service.createUser(item);
  update = async (itemWithId: WithDbId<AppUser>): Promise<void> => this.service.updateUser(itemWithId);
  doDelete = async (id: DbKey<AppUser>): Promise<void> => this.service.deleteUser(id);
  query = async (q: TableQuery): Promise<Paginated<WithDbId<AppUser>>> => this.service.queryUsers(q);

  queryList = async (appClientId: DbKey<AppClient>): Promise<Paginated<AppUserListEntry>> =>
    this.service.queryAppUserList(appClientId);
  queryData = async (appClientId: DbKey<AppClient>): Promise<UserData> => this.service.queryAppUserData(appClientId);
  createWithGlobalGroup = async (item: AppUser): Promise<DbKey<AppUser>> => this.service.createUserWithGroup(item);

  createManager = async (m: UserGroupManager): Promise<DbKey<UserGroupManager>> =>
    this.service.createUserGroupManager(m);
  deleteManager = async (id: DbKey<UserGroupManager>): Promise<void> => this.service.deleteUserGroupManager(id);
  queryManagers = async (q: TableQuery): Promise<Paginated<WithDbId<UserGroupManager>>> =>
    this.service.queryUserGroupManagers(q);

  createMember = async (m: UserGroupMember): Promise<DbKey<UserGroupMember>> => this.service.createUserGroupMember(m);
  deleteMember = async (id: DbKey<UserGroupMember>): Promise<void> => this.service.deleteUserGroupMember(id);
  queryMembers = async (q: TableQuery): Promise<Paginated<WithDbId<UserGroupMember>>> =>
    this.service.queryUserGroupMembers(q);

  createUserQuiz = async (m: UserQuiz): Promise<DbKey<UserQuiz>> => this.service.createUserQuiz(m);
  updateUserQuiz = async (m: WithDbId<UserQuiz>): Promise<void> => this.service.updateUserQuiz(m);
  deleteUserQuiz = async (id: DbKey<UserQuiz>): Promise<void> => this.service.deleteUserQuiz(id);

  purgeUserQuiz = async (id: DbKey<UserQuiz>): Promise<void> => this.service.purgeUserQuiz(id);
}

export class AppUserStore extends DataStore<AppUser> {
  @observable.ref list: Promise<Paginated<AppUserListEntry>>;

  @observable clientId: DbKey<AppClient>;
  /// Replaces base field 'selection'
  @observable user?: Promise<AppUserListEntry>;
  userData: Promise<UserData>;

  /// OK - assuming here that we *always* want this to be tied to *either* an AppClientId
  /// *or* an AppUserId (and therefore indirectly an AppClientId).
  /// Unlike the AppClientStore, which can be an open query, AppUserStore should
  /// not load unless a client or user is specified.
  public static fromService = (
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    appClientId: DbKey<AppClient>,
    id?: DbKey<AppUser>
  ): AppUserStore => {
    const store = new AppUserStore(new AppUserApi(service), identity, appClientId, id);
    return store;
  };

  public constructor(
    transport: TransportLayer<AppUser>,
    identity: LoggedIn | LoggedInSuper,
    appClientId: DbKey<AppClient>,
    id?: DbKey<AppUser>
  ) {
    super(transport, identity, id);
    this.clientId = appClientId;
    // Don't autoload all data, we might only want a subset (eg. setting a client ID) - hence not in base class
    this.data = this.init(id);
    if (id) {
      this.user = this.selectUser(id);
    }
    this.userData = this.initData();
    this.list = this.initList();
  }

  private readonly initList = () => {
    return this.loadList(this.clientId);
    // Don't call reload() here - or indeed anything else
    // this.data = this.load(c)
  };

  /** @override to filter on appClientId */
  init = async (id?: DbKey<AppUser>): Promise<Paginated<WithDbId<AppUser>>> => {
    if (this.authorized) {
      if (id) {
        return this.load(id);
      } else {
        const results = await this.query(this.queryId(this.clientId, 'appClientId'));
        return results;
      }
    } else {
      return Promise.reject('Not authorized');
    }
  };

  // @action
  // public setUser = async (appUserId: DbKey<AppUser>): Promise<void> => {
  //   if (this.authorized) {
  //     this.user = this.selectUser(appUserId)
  //   } else { return Promise.reject("Not authorized") }
  // }

  private readonly initData = () => {
    return this.loadData(this.clientId);
  };

  private readonly loadList = async (appClientId: DbKey<AppClient>): Promise<Paginated<AppUserListEntry>> => {
    if (this.authorizedForClient(appClientId)) {
      const results = await this.queryList(appClientId);
      return results;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadData = async (id: DbKey<AppClient>): Promise<UserData> => {
    if (this.clientId && this.authorizedForClient(this.clientId)) {
      const data = await this.queryData(id);
      return data;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly queryList = async (appClientId: DbKey<AppClient>): Promise<Paginated<AppUserListEntry>> => {
    const results = await (this.transport as AppUserApi).queryList(appClientId);
    return results;
  };

  private readonly queryData = async (appClientId: DbKey<AppClient>): Promise<UserData> => {
    const result = await (this.transport as AppUserApi).queryData(appClientId);
    return result;
  };

  @computed
  public get loadingList(): MaybeLoading<Paginated<AppUserListEntry>> {
    if (this.authorizedForClient(this.clientId)) {
      return fromPromise(this.list);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingData(): MaybeLoading<UserData> {
    if (this.authorizedForClient(this.clientId)) {
      return fromPromise(this.userData);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  /// Replaces base getter 'selected'
  @computed
  public get selectedUser(): MaybeLoading<AppUserListEntry> | undefined {
    if (this.authorizedForClient(this.clientId)) {
      if (this.user) {
        return fromPromise(this.user);
      } else {
        return undefined;
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  /// Adds to base method 'setSelected'
  private readonly selectUser = async (id: DbKey<AppUser>): Promise<AppUserListEntry> => {
    if (this.authorized) {
      const results = await (this.transport as AppUserApi).queryList(this.clientId);
      // TODO: dereferencing an item that may not exist in the array?
      return results.items.filter(e => e.id === id)[0];
    } else {
      return Promise.reject('Not authorized');
    }
  };

  // Override base call - DEFER REFRESH IS OBSOLETE
  public create = async (item: AppUser): Promise<DbKey<AppUser>> => {
    // FF2-463: Prevent ADMIN from creating or updating SUPER users - is enforced on the server
    if (!this.checkAuthorizedForUser(item)) {
      // Drop to USER
      item.role = ApplicationRole.user;
    }
    const appUserId = await this.transport.create(item);
    return appUserId;
  };

  // Override base call - DEFER REFRESH IS OBSOLETE
  public update = async (item: WithDbId<AppUser>): Promise<void> => {
    const user = { ...item };
    /// Special action: Do not set current user role to anything other than SUPER. Enforced on server
    if (user.id === this.identity.appUserWithDbId.id) {
      if (this.identity.kind === 'super-logged-in') {
        if (user.value.role !== ApplicationRole.super) {
          user.value.role = ApplicationRole.super;
        }
      } else if (this.checkAuthorizedForUser(user.value)) {
        if (user.value.role !== ApplicationRole.admin) {
          user.value.role = ApplicationRole.admin;
        }
      }
      // SS-630: Prevent a user from suspending or deleting itself - also enforced on server
      if (user.value.status !== AppUserStatus.active) {
        user.value.status = AppUserStatus.active;
      }
    }
    // FF2-463: Prevent ADMIN from creating or updating SUPER users, or ADMIN users on other clients
    // - enforced on the server
    if (!this.checkAuthorizedForUser(user.value)) {
      // Drop to USER
      user.value.role = ApplicationRole.user;
    }
    await this.transport.update(user);
  };

  public createWithGroup = async (item: AppUser): Promise<DbKey<AppUser>> => {
    // FF2-463: Prevent ADMIN from creating or updating SUPER users - is enforced on the server
    if (!this.checkAuthorizedForUser(item)) {
      // Drop to USER
      item.role = ApplicationRole.user;
    }
    const appUserId = await (this.transport as AppUserApi).createWithGlobalGroup(item);
    return appUserId;
  };

  public updateUserQuizzes = async (userId: DbKey<AppUser>, updates: UserQuizChange[]): Promise<void> => {
    if (this.authorized) {
      await Promise.all(
        updates.map(async change => {
          if (change.kind === 'create') {
            await this.createUserQuiz(
              // TODO: DueAt
              makeUserQuiz({ appUserId: userId, quizId: change.value, startedAt: null, completedAt: null, dueAt: null })
            );
          } else if (change.kind === 'delete') {
            await this.deleteUserQuiz(change.value);
            // TODO: else if (change.kind === "update") { await onUpdateUserQuiz { ..., dueAt: change.dueAt}};
          } else if (change.kind === 'purge') {
            await this.purgeUserQuiz(change.value);
          }
        })
      );
    } else {
      return Promise.reject('Not authorized');
    }
  };

  public updateUserGroups = async (userId: DbKey<AppUser>, updates: UserGroupChange[]): Promise<void> => {
    if (this.authorized) {
      await Promise.all(
        updates.map(async change => {
          if (change.kind === 'create') {
            if (change.managerOf) {
              await this.createManager(makeUserGroupManager({ appUserId: userId, userGroupId: change.groupId }));
            }
            if (change.memberOf) {
              await this.createMember(makeUserGroupMember({ appUserId: userId, userGroupId: change.groupId }));
            }
          } else if (change.kind === 'delete') {
            if (change.managerOf) {
              await this.deleteManager(change.managerOf);
            }
            if (change.memberOf) {
              await this.deleteMember(change.memberOf);
            }
          }
        })
      );
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly createManager = async (item: UserGroupManager): Promise<DbKey<UserGroupManager>> => {
    const mgrId = await (this.transport as AppUserApi).createManager(item);
    return mgrId;
  };

  private readonly deleteManagers = async (a: DbKey<UserGroupManager>[]): Promise<void> => {
    for (const id of a) {
      await (this.transport as AppUserApi).deleteManager(id);
    }
  };

  private readonly deleteManager = async (id: DbKey<UserGroupManager>): Promise<void> => {
    await (this.transport as AppUserApi).deleteManager(id);
  };

  private readonly createMember = async (item: UserGroupMember): Promise<DbKey<UserGroupMember>> => {
    const mbrId = await (this.transport as AppUserApi).createMember(item);
    return mbrId;
  };

  private readonly deleteMembers = async (a: DbKey<UserGroupMember>[]): Promise<void> => {
    for (const id of a) {
      await (this.transport as AppUserApi).deleteMember(id);
    }
  };

  private readonly deleteMember = async (id: DbKey<UserGroupMember>): Promise<void> => {
    await (this.transport as AppUserApi).deleteMember(id);
  };

  private readonly createUserQuiz = async (m: UserQuiz): Promise<DbKey<UserQuiz>> => {
    const uqId = await (this.transport as AppUserApi).createUserQuiz(m);
    return uqId;
  };

  // TODO: currently unused
  _updateUserQuiz = async (m: WithDbId<UserQuiz>): Promise<void> => {
    await (this.transport as AppUserApi).updateUserQuiz(m);
  };

  private readonly deleteUserQuiz = async (id: DbKey<UserQuiz>): Promise<void> => {
    await (this.transport as AppUserApi).deleteUserQuiz(id);
  };

  private readonly purgeUserQuiz = async (id: DbKey<UserQuiz>): Promise<void> => {
    await (this.transport as AppUserApi).purgeUserQuiz(id);
  };

  // TODO: currently unused
  _deleteUsersItems = async (a: DbKey<AppUser>[]): Promise<void> => {
    // Delete managers and members
    const managers = await this.loadManagersForUserIds(a);
    const members = await this.loadMembersForUserIds(a);
    /// --- [ TODO: Delete/purge UserQuiz - BUT only if confirmed ] ---

    const managerIds = managers.items.map(mgr => mgr.id);
    const memberIds = members.items.map(mbr => mbr.id);

    await this.deleteManagers(managerIds);
    await this.deleteMembers(memberIds);
  };

  private readonly loadManagersForUserIds = async (
    userIds: DbKey<AppUser>[]
  ): Promise<Paginated<WithDbId<UserGroupManager>>> => {
    const managers = await (this.transport as AppUserApi).queryManagers(
      makeTableQuery({
        filter: {
          kind: 'in',
          value: { field: 'appUserId', values: userIds }
        }
      })
    );
    return managers;
  };

  private readonly loadMembersForUserIds = async (
    userIds: DbKey<AppUser>[]
  ): Promise<Paginated<WithDbId<UserGroupMember>>> => {
    const members = await (this.transport as AppUserApi).queryMembers(
      makeTableQuery({
        filter: {
          kind: 'in',
          value: { field: 'appUserId', values: userIds }
        }
      })
    );
    return members;
  };

  // FF2-463: Prevent ADMIN from creating, updating, deleting or querying SUPER users
  // This is enforced on the Server (throw HTTP-500), including if users on
  // any other client
  private readonly checkAuthorizedForUser = (u: AppUser): boolean => {
    if (this.identity.kind === 'super-logged-in') {
      // If superuser - OK
      return true;
    } else {
      if (this.authorizedForClient(this.clientId)) {
        if (u.role === ApplicationRole.super) {
          return false;
        } else {
          return true;
        }
      }
    }
    // Otherwise never authorized
    return false;
  };
}



// WEBPACK FOOTER //
// ./src/admin/stores/app-user-store.ts